/**
 * Data layer for `affiliate-manager`. Thin typed fetch wrappers over
 * /api/v1/affiliate-manager.
 *
 * BE endpoints (see apps/api/src/routes/affiliate-manager.ts):
 *   POST   /                       — assign
 *   GET    /:affiliate_id          — list assignments for an affiliate
 *   GET    /by-manager/:manager_id — list affiliates owned by a manager
 *   DELETE /:id                    — unassign by id
 *
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export type AffiliateManagerRole = "primary" | "backup";

export interface AffiliateManagerRow {
	id: string;
	tenant_id: string;
	affiliate_id: string;
	manager_id: string;
	role: AffiliateManagerRole;
	assigned_at: number;
	assigned_by: string;
}

export interface ListByAffiliateResponse {
	affiliate_id: string;
	count: number;
	assignments: AffiliateManagerRow[];
}

export interface ListByManagerResponse {
	manager_id: string;
	workload: number;
	assignments: AffiliateManagerRow[];
}

export interface CreateAssignmentBody {
	id: string;
	affiliate_id: string;
	manager_id: string;
	role?: AffiliateManagerRole;
}

export interface CreateAssignmentResponse {
	assignment: AffiliateManagerRow;
	reassigned: boolean;
}

const BASE = "/api/v1/affiliate-manager";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listByAffiliate(
	affiliateId: string,
): Promise<ListByAffiliateResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(affiliateId)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListByAffiliateResponse>(res);
}

export async function listByManager(
	managerId: string,
): Promise<ListByManagerResponse> {
	const res = await fetch(
		`${BASE}/by-manager/${encodeURIComponent(managerId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<ListByManagerResponse>(res);
}

export async function create(
	body: CreateAssignmentBody,
): Promise<CreateAssignmentResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateAssignmentResponse>(res);
}

export async function remove(id: string): Promise<{ ok: boolean; id: string }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: boolean; id: string }>(res);
}

export const affiliateManagerQueryKeys = {
	all: ["affiliate-manager"] as const,
	byAffiliate: (affiliateId: string) =>
		[...affiliateManagerQueryKeys.all, "by-affiliate", affiliateId] as const,
	byManager: (managerId: string) =>
		[...affiliateManagerQueryKeys.all, "by-manager", managerId] as const,
};
